<script setup lang="ts">
import { useField, useForm } from 'vee-validate'
import { computed, ref, watch } from 'vue'
import * as yup from 'yup'
import { type UpdateServicePlanPayload, updateServicePlanAction } from '@/api/functions/services/service-plan.action'
import type { ServicePlan } from '@/api/typings/service.typing'
import { useMutationApi } from '@/composables/useApi'
import { useToast } from '@/composables/useToast'
import { getErrorMessage } from '@/utils/errorUtils'

interface ServicePlanRegion {
  region: string
  amount: number | null
  currency: string
}

interface RegionOption {
  label: string
  value: string
  flagUrl: string
  iso: string
}

const props = defineProps<{
  modelValue: boolean
  serviceSlug: string | undefined
  selectedPlan: ServicePlan | null
  loading?: boolean
  countries: {
    label: string
    value: string
    flagUrl: string
    iso: string
  }[]
  currencies: {
    id: string
    isoCode: string
  }[]
}>()

const emit = defineEmits<{
  (e: 'update:modelValue', value: boolean): void
  (e: 'confirm', value: UpdateServicePlanPayload): void
  (e: 'refetch'): void
}>()

const toast = useToast()

const schema = yup.object({
  name: yup
    .string()
    .trim()
    .required('نام پلن را وارد کنید')
    .min(2, 'نام پلن باید حداقل 2 کاراکتر باشد')
    .max(100, 'نام پلن نمی‌تواند بیشتر از 100 کاراکتر باشد'),

  duration: yup
    .number()
    .transform((value, originalValue) => (originalValue === '' || originalValue === null ? undefined : value))
    .typeError('مدت اعتبار باید عدد باشد')
    .required('مدت اعتبار الزامی است')
    .integer('مدت اعتبار باید عدد صحیح باشد')
    .moreThan(0, 'مدت اعتبار باید بزرگ‌تر از صفر باشد'),

  sortOrder: yup
    .number()
    .transform((value, originalValue) => (originalValue === '' || originalValue === null ? undefined : value))
    .typeError('ترتیب نمایش باید عدد باشد')
    .required('ترتیب نمایش الزامی است')
    .integer('ترتیب نمایش باید عدد صحیح باشد')
    .min(0, 'ترتیب نمایش نمی‌تواند منفی باشد'),

  description: yup.string().max(2000, 'توضیحات نمی‌تواند بیشتر از 2000 کاراکتر باشد'),

  status: yup.string().oneOf(['ACTIVE', 'INACTIVE'], 'وضعیت معتبر نیست').required('وضعیت الزامی است'),

  hasRegion: yup.boolean().required(),

  selectedRegions: yup
    .array()
    .of(yup.string().required())
    .when('hasRegion', {
      is: true,
      then: (schema) => schema.min(1, 'حداقل یک ریجن را انتخاب کنید').required('حداقل یک ریجن را انتخاب کنید'),
      otherwise: (schema) => schema,
    }),

  planAmount: yup
    .number()
    .transform((value, originalValue) => (originalValue === '' || originalValue === null ? undefined : value))
    .when('hasRegion', {
      is: false,
      then: (schema) =>
        schema
          .typeError('مبلغ باید عدد باشد')
          .required('مبلغ را وارد کنید')
          .moreThan(0, 'مبلغ باید بزرگ‌تر از صفر باشد'),
      otherwise: (schema) => schema.nullable().notRequired(),
    }),

  planCurrency: yup.string().when('hasRegion', {
    is: false,
    then: (schema) => schema.required('ارز پلن را انتخاب کنید'),
    otherwise: (schema) => schema.notRequired(),
  }),

  planRegions: yup
    .array()
    .of(
      yup.object({
        region: yup.string().required(),
        amount: yup
          .number()
          .transform((value, originalValue) => (originalValue === '' || originalValue === null ? undefined : value))
          .typeError('مبلغ باید عدد باشد')
          .required('مبلغ این ریجن را وارد کنید')
          .moreThan(0, 'مبلغ باید بزرگ‌تر از صفر باشد'),
        currency: yup.string().required('ارز این ریجن را انتخاب کنید'),
      })
    )
    .when('hasRegion', {
      is: true,
      then: (schema) => schema.min(1, 'حداقل یک ریجن را انتخاب کنید').required(),
      otherwise: (schema) => schema,
    }),
})

const {
  handleSubmit,
  errors,
  resetForm: resetValidationForm,
  validateField,
  setFieldValue,
} = useForm({
  validationSchema: schema,
  initialValues: {
    name: '',
    duration: null as number | null,
    sortOrder: 0,
    description: '',
    status: 'ACTIVE',
    hasRegion: false,
    selectedRegions: [] as string[],
    planAmount: null as number | null,
    planCurrency: '',
    planRegions: [] as ServicePlanRegion[],
  },
})

const { value: planName } = useField<string>('name')
const { value: planDuration } = useField<number | null>('duration')
const { value: planSortOrder } = useField<number>('sortOrder')
const { value: planDescription } = useField<string>('description')
const { value: planStatus } = useField<string>('status')
const { value: hasRegion } = useField<boolean>('hasRegion')
const { value: selectedRegions } = useField<string[]>('selectedRegions')
const { value: planAmount } = useField<number | null>('planAmount')
const { value: planCurrency } = useField<string>('planCurrency')
const { value: planRegions } = useField<ServicePlanRegion[]>('planRegions')

const statusOptions = [
  { label: 'فعال', value: 'ACTIVE' },
  { label: 'غیرفعال', value: 'INACTIVE' },
]

const currencyOptions = computed(() =>
  props.currencies.map((currency) => ({
    label: currency.isoCode,
    value: currency.id,
  }))
)

const regionOptions = computed<RegionOption[]>(() => {
  const options: RegionOption[] = [...props.countries]

  const prices = props.selectedPlan?.prices ?? []

  prices.forEach((price) => {
    const region = price.region

    if (!region?.id) return

    const exists = options.some((item) => item.value === region.id)

    if (!exists) {
      options.push({
        label: region.name,
        value: region.id,
        flagUrl: '',
        iso: region.code ?? '',
      })
    }
  })

  return options
})

const createPlanMutation = useMutationApi({
  mutationFn: (payload: UpdateServicePlanPayload) => {
    if (!props.serviceSlug) {
      throw new Error('شناسه سرویس معتبر نیست')
    }

    if (!props.selectedPlan?.id) {
      throw new Error('پلن معتبری برای ویرایش انتخاب نشده است')
    }

    return updateServicePlanAction(props.serviceSlug, props.selectedPlan.id, payload)
  },

  onError: (error: Error) => {
    toast.error(getErrorMessage(error), 'خطا')
  },
})

const isSubmitting = computed(() => createPlanMutation.isPending.value || !!props.loading)

const resetForm = () => {
  resetValidationForm()
}

const closeModal = () => {
  if (isSubmitting.value) return

  emit('update:modelValue', false)
  resetForm()
}

const setFormValues = (plan: ServicePlan) => {
  const prices = plan.prices ?? []
  const regionalPrices = prices.filter((price) => price.region?.id)
  const globalPrice = prices.find((price) => !price.region?.id)

  const regions: ServicePlanRegion[] = regionalPrices.map((price) => ({
    region: price.region!.id,
    amount: Number(price.amount),
    currency: price.currency?.id ?? '',
  }))

  resetValidationForm({
    values: {
      name: plan.name ?? '',
      duration: plan.duration_days ?? null,
      sortOrder: plan.sort_order ?? 0,
      description: plan.description ?? '',
      status: plan.is_active ? 'ACTIVE' : 'INACTIVE',
      hasRegion: regionalPrices.length > 0,
      selectedRegions: regions.map((region) => region.region),
      planAmount: globalPrice ? Number(globalPrice.amount) : null,
      planCurrency: globalPrice?.currency?.id ?? '',
      planRegions: regions,
    },
  })
}

watch(
  () => [props.selectedPlan, props.modelValue] as const,
  ([plan, isOpen]) => {
    if (isOpen && plan) {
      setFormValues(plan)
    } else if (!isOpen) {
      resetForm()
    }
  },
  { immediate: true }
)

watch(hasRegion, (value) => {
  if (value) {
    setFieldValue('planAmount', null)
    setFieldValue('planCurrency', '')
  } else {
    setFieldValue('selectedRegions', [])
    setFieldValue('planRegions', [])
  }
})

const handleRegionsChange = (regions: string[]) => {
  const updatedRegions = regions.map((region) => {
    const existingRegion = planRegions.value.find((item) => item.region === region)

    return (
      existingRegion || {
        region,
        amount: null,
        currency: '',
      }
    )
  })

  setFieldValue('planRegions', updatedRegions)
  validateField('selectedRegions')
  validateField('planRegions')
}

const updatePlan = handleSubmit(async (values) => {
  const prices: UpdateServicePlanPayload['prices'] = values.hasRegion
    ? values.planRegions.map((region) => ({
        pricing_type: 'fixed',
        region_id: region.region,
        currency_id: region.currency,
        amount: String(region.amount),
      }))
    : [
        {
          pricing_type: 'fixed',
          region_id: null,
          currency_id: values.planCurrency,
          amount: String(values.planAmount),
        },
      ]

  const payload: UpdateServicePlanPayload = {
    name: values.name.trim(),
    description: values.description.trim() || null,
    duration_days: Number(values.duration),
    sort_order: Number(values.sortOrder),
    is_active: values.status === 'ACTIVE',
    prices,
  }

  try {
    await createPlanMutation.mutateAsync(payload)

    emit('confirm', payload)
    emit('refetch')
    emit('update:modelValue', false)

    resetForm()
    toast.success('پلن با موفقیت ویرایش شد')
  } catch {}
})
</script>

<template>
    <Modal :model-value="props.modelValue" @update:model-value="emit('update:modelValue', $event)" width="700px"
        background="#FFF" :closable="!isSubmitting" title="ویرایش پلن" closeDirection="left">
        <form @submit.prevent="updatePlan" class="flex flex-col min-h-[600px]">
            <div class="flex-1 overflow-y-auto px-4 py-6 pb-9">
                <div class="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    <Input v-model="planName" name="name" label="نام پلن" type="text" :error="errors.name" />

                    <Input v-model="planDuration" name="duration" label="مدت اعتبار (روز)" type="number"
                        :error="errors.duration" />

                    <Input v-model="planSortOrder" name="sortOrder" label="ترتیب نمایش" type="number"
                        :error="errors.sortOrder" />

                    <Select v-model="planStatus" name="status" label="وضعیت" :options="statusOptions"
                        :error="errors.status" />

                    <div class="lg:col-span-2">
                        <Textarea v-model="planDescription" name="description" label="توضیحات پلن" type="text"
                            :error="errors.description" />
                    </div>

                    <div class="lg:col-span-2 mt-16 mb-2">
                        <Checkbox v-model="hasRegion" name="hasRegion" label="این پلن دارای ریجن است" color="#1b325a"
                            labelColor="#1b325a" />
                    </div>

                    <template v-if="!hasRegion">
                        <div>
                            <Input v-model="planAmount" name="planAmount" label="مبلغ" type="number"
                                :error="errors.planAmount" />
                        </div>

                        <div>
                            <Select v-model="planCurrency" name="planCurrency" label="ارز" :options="currencyOptions"
                                :error="errors.planCurrency" />
                        </div>
                    </template>

                    <template v-else>
                        <div class="lg:col-span-2">
                            <MultiSelect v-model="selectedRegions" variant="checkbox" name="selectedRegions"
                                label="ریجن" :options="regionOptions" @update:modelValue="handleRegionsChange" />

                            <p v-if="errors.selectedRegions" class="text-sm text-red-500 mt-1">
                                {{ errors.selectedRegions }}
                            </p>
                        </div>

                        <div v-for="(region, index) in planRegions" :key="region.region"
                            class="lg:col-span-2 grid lg:grid-cols-2 gap-6 p-4 rounded-xl bg-[#f7f9fc] border border-[#e5eaf2]">
                            <Input v-model="region.amount" :name="`planRegions[${index}].amount`"
                                :label="`مبلغ ریجن ${regionOptions.find(item => item.value === region.region)?.label ?? ''}`"
                                type="number" :error="errors[`planRegions[${index}].amount`]" />

                            <Select v-model="region.currency" :name="`planRegions[${index}].currency`"
                                :label="`ارز ریجن ${regionOptions.find(item => item.value === region.region)?.label ?? ''}`"
                                :options="currencyOptions" :error="errors[`planRegions[${index}].currency`]" />
                        </div>
                    </template>
                </div>
            </div>

            <div class="sticky bottom-0 bg-white z-50 flex justify-between gap-6 w-full pt-3 pb-9 px-2 border-gray-200">
                <Button type="button" @click="closeModal" variant="outline" label="بازگشت" :isAdmin="true"
                    :disabled="isSubmitting" class="w-4/12 h-12 rounded-xl border-2" />

                <Button type="submit" label="ذخیره تغییرات" :isAdmin="true" :loading="isSubmitting"
                    :disabled="isSubmitting" class="w-9/12 h-12 rounded-xl" />
            </div>
        </form>
    </Modal>
</template>